/**
 * Endpoints du domaine auth — source canonique (user-service, guide §6.9).
 *
 * Exposés en `/gw/user/api/v1/` et PUBLICS (pas de Bearer) — la route
 * `/gw/user` du gateway porte la policy `Anonymous`.
 *
 * `@/core/api/endpoints` ré-exporte ces valeurs (pas de duplication) : toute
 * nouvelle route auth s'ajoute ICI, jamais dans core.
 */
export const authEndpoints = {
  login: "/gw/user/api/v1/Auth/login",
  refresh: "/gw/user/api/v1/Auth/refresh",
  logout: "/gw/user/api/v1/Auth/logout",
  forgotPassword: "/gw/user/api/v1/Passwords/forgot",
  /**
   * Mot de passe oublié par SMS (guide v4.1 §6.9) — flux public 3 étapes à
   * destination de l'app mobile client. Agents / admin / service ne reçoivent
   * jamais de code SMS (facteur trop faible : ils utilisent `forgotPassword`
   * par email). Déclaré pour parité ; aucun écran back-office à ce jour
   * (décision 46 du plan 2026-10-28).
   */
  passwords: {
    /** POST — PUBLIC. Toujours 202 (anti-énumération). Quotas 60 s / 5h. */
    forgotPhone: "/gw/user/api/v1/Passwords/forgot/phone",
    /** POST — PUBLIC. Code SMS → `resetToken` (10 min, usage unique). */
    verifyPhone: "/gw/user/api/v1/Passwords/verify/phone",
    /** POST — PUBLIC. Fixe le nouveau mot de passe (204, sessions révoquées). */
    resetPhone: "/gw/user/api/v1/Passwords/reset/phone",
  },
  /** POST — PUBLIC. Auto-inscription / enrôlement assisté (étape 1 KYC). */
  addUser: "/gw/user/api/v1/Users/add",
  /**
   * Changement du mot de passe en session (guide v4.1 §6.9) — AUTHENTIFIÉ.
   * Vérifie l'ancien mot de passe par une authentification Keycloak réelle
   * (brute-force du realm applicable, un jeton dérobé ne suffit pas). Révoque
   * toutes les sessions au 204 — l'application se reconnecte via `login`.
   */
  changeMyPassword: "/gw/user/api/v1/Users/me/password",
  /**
   * OTP de vérification pré-inscription (guide v2.3 §6.9).
   * ENDPOINTS PUBLICS : la vérification du téléphone/email précède la création
   * du compte, donc l'existence d'un jeton. Encadrés par 3 quotas par
   * destinataire (60 s mini entre envois, 5/h, 5 tentatives de vérif).
   */
  otp: {
    /** POST — PUBLIC. channel = SMS | EMAIL | BOTH. */
    send: "/gw/user/api/v1/Otp/send",
    /** POST — PUBLIC. Usage unique (purge tous les canaux après succès). */
    verify: "/gw/user/api/v1/Otp/verify",
  },
} as const;
